/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { TeamOutlined } from '@ant-design/icons'
import { Spin } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import { PLAYER_FINDER_SUPPORT_URL } from '@sportninja/common/features/playerFinder/constants'
import type { PositionNameResolver } from '@sportninja/common/features/playerFinder/candidateDisplay'
import type { SpareCandidate } from '@sportninja/common/features/playerFinder/types'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import React from 'react'
import {
  font,
  paragraphSmall,
  weightBold,
  weightMedium,
} from 'src/components/css'
import { useInfiniteScroll } from 'src/hooks/useInfiniteScroll'
import { MODAL_TOKENS } from 'src/styles/modalTokens'
import PlayerFinderSkeleton from './PlayerFinderSkeleton'
import SpareCandidateCard from './SpareCandidateCard'

export interface SpareCandidateListProps {
  candidates: SpareCandidate[]
  isLoading: boolean
  isError: boolean
  /** More pages to load as the end of the list scrolls into view. */
  hasNextPage: boolean
  isFetchingNextPage: boolean
  onLoadMore: () => void
  resolvePositionName?: PositionNameResolver
  isSelected: (id: string) => boolean
  onToggle: (id: string) => void
  onOpenCandidate: (candidate: SpareCandidate) => void
}

const SpareCandidateList: React.FC<SpareCandidateListProps> = ({
  candidates,
  isLoading,
  isError,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
  resolvePositionName,
  isSelected,
  onToggle,
  onOpenCandidate,
}) => {
  'use memo'
  // Watches an element after the last card; once it nears the view (including a
  // list too short to scroll) the next page loads.
  const { sentinelRef } = useInfiniteScroll({
    onLoadMore,
    hasMore: hasNextPage,
    isLoading: isFetchingNextPage,
  })

  if (isLoading) {
    return (
      <div css={scrollStyle}>
        <div css={fullRowStyle}>
          <PlayerFinderSkeleton />
        </div>
      </div>
    )
  }

  if (isError) {
    return (
      <div css={messageStyle}>
        <div css={emptyTitleStyle}>
          {capitalizeWords('Couldn’t Load Spare Players')}
        </div>
        <div css={emptyHintStyle}>Please try again.</div>
      </div>
    )
  }

  if (candidates.length === 0) {
    return (
      <div css={messageStyle}>
        <TeamOutlined css={emptyIconStyle} />
        <div css={emptyTitleStyle}>
          {capitalizeWords('No Eligible Spare Players Found')}
        </div>
        {/* Matches the mobile empty state: hint sentence with the support link
            on its own line below. Web says "Click Here." where mobile says
            "Tap Here." */}
        <div css={emptyHintStyle}>
          For more information about this feature,
          <br />
          <a
            css={learnMoreStyle}
            href={PLAYER_FINDER_SUPPORT_URL}
            target='_blank'
            rel='noreferrer'
          >
            Click Here.
          </a>
        </div>
      </div>
    )
  }

  return (
    <div css={scrollStyle}>
      {candidates.map((candidate) => (
        <SpareCandidateCard
          key={candidate.id}
          candidate={candidate}
          selected={isSelected(candidate.id)}
          resolvePositionName={resolvePositionName}
          onToggle={() => onToggle(candidate.id)}
          onClick={() => onOpenCandidate(candidate)}
        />
      ))}
      <div ref={sentinelRef} css={fullRowStyle} aria-hidden />
      {isFetchingNextPage && (
        <div css={[fullRowStyle, nextPageStyle]}>
          <Spin size='small' />
        </div>
      )}
    </div>
  )
}

export default SpareCandidateList

/* Recessed well: one step DARKER than the modal body so the cards inside read
   as raised surfaces. Two columns on desktop, collapsing to one on narrow
   viewports. Cards STRETCH to the row's height (grid's default `stretch`, not
   `start`) — that shared height is what lets each card push its divider and
   Available Days / Rate row to a common baseline via `margin-top: auto`. */
const scrollStyle = css`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  max-height: 52vh;
  overflow-y: auto;
  padding: 16px;
  border-radius: 12px;
  background-color: ${colors.SECONDARY_400};

  @media (max-width: ${MODAL_TOKENS.MOBILE_BREAKPOINT}px) {
    grid-template-columns: minmax(0, 1fr);
  }
`

const messageStyle = css`
  ${font.body}
  ${paragraphSmall}
  color: ${colors.NEUTRAL_50};
  padding: 32px 24px;
  text-align: center;
`

/* Empty state: mirrors the mobile layout — icon, Title Case headline, then the
   hint sentence with the support link on its own line. */
const emptyIconStyle = css`
  color: ${colors.NEUTRAL_50};
  display: block;
  font-size: 40px;
  margin-bottom: 4px;
`

const emptyTitleStyle = css`
  ${font.title}
  ${weightBold}
  color: ${colors.WHITE};
  font-size: 20px;
  line-height: 24px;
  margin-bottom: 6px;
`

const emptyHintStyle = css`
  color: ${colors.NEUTRAL_50};
`

const learnMoreStyle = css`
  ${weightMedium}
  color: ${colors.PRIMARY};

  &:hover {
    color: ${colors.PRIMARY};
    text-decoration: underline;
  }
`

/* Full-width row inside the two-column grid (skeleton, paging row). */
const fullRowStyle = css`
  grid-column: 1 / -1;
`

const nextPageStyle = css`
  display: flex;
  justify-content: center;
  padding: 12px 0;
`
